import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageCircle, Mail, PenTool, Monitor, Box, Smartphone, Landmark, Play,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import SEO from "../components/SEO";
import ARModal from "../components/ARModal";
import { SITE, AR_LINKS, whatsappLink } from "../content";

const A = (p) => `${process.env.PUBLIC_URL}${p}`;

const SectionLabel = ({ num, title, dark = false }) => (
  <div
    className={`flex items-center gap-4 text-xs font-medium tracking-[0.3em] uppercase ${dark ? "text-neutral-400" : "text-neutral-500"}`}
    data-testid={`section-label-${num}`}
  >
    <span className="text-brand-red">{num}</span>
    <span className={`h-px w-10 ${dark ? "bg-neutral-700" : "bg-neutral-300"}`} aria-hidden="true" />
    <span>{title}</span>
  </div>
);

const ArrowCta = ({ to, href, children, testid, dark = false }) => {
  const cls = `inline-flex items-center gap-2 text-[13px] font-medium uppercase tracking-[0.2em] link-underline ${
    dark ? "text-white" : "text-neutral-900"
  }`;
  const icon = <ArrowRight size={15} strokeWidth={1.5} className="text-brand-red" />;
  if (to) return <Link to={to} className={cls} data-testid={testid}>{children}{icon}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>{children}{icon}</a>;
};

const CAPABILITIES = [
  { label: "Branding", icon: PenTool },
  { label: "Websites", icon: Monitor },
  { label: "3D", icon: Box },
  { label: "AR", icon: Smartphone },
  { label: "Exhibitions", icon: Landmark },
  { label: "VPV", icon: Play },
];

/* 01 HERO */
const Hero = () => (
  <section className="relative border-b border-neutral-200 overflow-hidden" data-testid="hero-section">
    {/* large right visual, top aligned to the header, bottom aligned to the capability row */}
    <div
      className="hidden lg:block absolute right-0 top-0 w-[56vw] pointer-events-none"
      style={{ height: "calc(100% - 260px)" }}
      aria-hidden="true"
    >
      <img
        src={A("/assets/hero-scene.png")}
        alt=""
        className="w-full h-full object-cover object-right-top hero-fade"
      />
    </div>

    <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="pt-10 md:pt-12 lg:pt-10">
        <div className="w-full lg:max-w-[45%]">
          <Reveal>
            <p className="text-[11px] md:text-xs font-medium tracking-[0.3em] uppercase text-neutral-500 leading-loose" data-testid="hero-qualifier">
              For manufacturers with<br />multiple factories, products &amp; export markets
            </p>
            <h1 className="mt-7 font-display text-5xl sm:text-6xl lg:text-[3.8rem] xl:text-[4.2rem] font-semibold tracking-tight leading-[1.06] text-black" data-testid="hero-heading">
              You&rsquo;ve built<br />a big business.
              <span className="block text-brand-red mt-3">Now make it<br />look like one.</span>
            </h1>
            <p className="mt-6 text-lg font-light text-neutral-600 max-w-sm leading-relaxed">
              We help manufacturers build brands, experiences and digital tools that make them stand out, look global and create impact.
            </p>
            <div className="mt-7 flex items-stretch border-y border-neutral-200 divide-x divide-neutral-200" data-testid="hero-capabilities">
              {CAPABILITIES.map((c) => (
                <div key={c.label} className="flex flex-col items-center gap-2.5 px-3 md:px-4 py-4 flex-1 min-w-[64px]">
                  <c.icon size={19} strokeWidth={1} className="text-neutral-800" />
                  <span className="text-[10px] md:text-[11px] font-medium tracking-[0.12em] uppercase text-neutral-600">{c.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-4">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="bg-brand-red hover:bg-black text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-3"
              >
                <MessageCircle size={16} strokeWidth={1.75} /> Talk to Nitin on WhatsApp <ArrowRight size={15} strokeWidth={1.5} />
              </a>
              <Link
                to="/work"
                data-testid="hero-see-work-button"
                className="border border-black text-black hover:bg-black hover:text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-3"
              >
                See Our Work <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      {/* visual below content on smaller screens */}
      <div className="lg:hidden pb-12">
        <img
          src={A("/assets/hero-scene.png")}
          alt="Manufacturing plant with brand communication across print, laptop and mobile"
          className="w-full h-auto"
          data-testid="hero-visual"
          fetchpriority="high"
        />
      </div>

      <div className="border-t border-neutral-200 mt-12 lg:mt-16 py-5 flex flex-col md:flex-row justify-between gap-2 text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500">
        <span data-testid="hero-strip-left">Brands for a stronger tomorrow</span>
        <span data-testid="hero-strip-right">India &nbsp;|&nbsp; The World &nbsp;|&nbsp; A Stronger Tomorrow</span>
      </div>
    </div>
  </section>
);

/* 02 OUR WORK */
const WORK_FEATURES = [
  {
    client: "Kohler India",
    services: ["Brand Experience", "3D", "Digital", "Retail"],
    note: "2 million likes on our designs",
    image: "/assets/lifestyle-premium-bathroom-11-copy.webp",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]",
    fit: "cover",
  },
  {
    client: "Param Dairy",
    services: ["Booth Design", "Branding", "Experience"],
    image: "/assets/final-impact-of-exhibition.png",
    span: "lg:col-span-5",
    aspect: "aspect-[4/3]",
    fit: "cover",
    to: "/case-studies/param-dairy-branding",
  },
  {
    client: "ABB",
    services: ["3D Product Application", "3D Rendering"],
    image: "/assets/product-renders-2cds383001r0065-c-copy-scaled.webp",
    span: "lg:col-span-5",
    aspect: "aspect-[4/3]",
    fit: "contain",
  },
  {
    client: "VPV",
    services: ["Virtual Plant Visit", "360°", "Interactive Factory Experience"],
    image: "/assets/vpv-dashboard-param-dairy.png",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]",
    fit: "cover",
    to: "/vpv",
  },
];

const WORK_INDEX = [
  { client: "HSN Fasteners", services: ["Brochure Design", "USP & Business Positioning", "Product Communication"] },
  { client: "BMW India", services: ["Showroom Design", "3D Visualisation"] },
  { client: "NTPC", services: ["Thermal Power Plant", "3D Visualisation", "Presentation & Virtual Model"] },
  { client: "Airport Authority of India", services: ["3D Airport Models", "Virtual Airport Models", "Digital Experience"] },
];

const OurWork = () => (
  <section className="border-b border-neutral-200" data-testid="our-work-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="02" title="Our Work" />
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black" data-testid="our-work-heading">
              Real companies.<br />Real impact.
            </h2>
            <p className="mt-5 text-lg font-light text-neutral-600 max-w-md">
              Brands, experiences and ideas built for manufacturers, exporters and product companies.
            </p>
          </div>
          <ArrowCta to="/work" testid="our-work-view-all">View All Projects</ArrowCta>
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="our-work-grid">
        {WORK_FEATURES.map((w, i) => (
          <Reveal key={w.client} delay={i * 0.06} className={w.span}>
            <Link to={w.to || "/work"} className="group block" data-testid={`work-feature-${w.client.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`${w.aspect} ${w.fit === "contain" ? "bg-neutral-50 flex items-center justify-center p-10" : "img-zoom"} overflow-hidden`}>
                <img
                  src={A(w.image)}
                  alt={`${w.client} project by The Next Design`}
                  loading="lazy"
                  decoding="async"
                  className={w.fit === "contain" ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"}
                />
              </div>
              <div className="mt-5 flex items-baseline justify-between gap-4 border-t border-neutral-200 pt-4">
                <h3 className="font-display text-xl font-normal tracking-tight text-black group-hover:text-brand-red transition-colors">{w.client}</h3>
                <p className="text-[11px] font-medium tracking-[0.15em] uppercase text-neutral-500 text-right">{w.services.join("  ·  ")}</p>
              </div>
              {w.note && (
                <p className="mt-2 text-[12px] font-medium tracking-[0.1em] uppercase text-brand-red" data-testid={`work-feature-note-${w.client.toLowerCase().replace(/\s+/g, "-")}`}>{w.note}</p>
              )}
            </Link>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 border-t border-neutral-200" data-testid="work-index">
        {WORK_INDEX.map((w, i) => (
          <Reveal key={w.client} delay={i * 0.04}>
            <Link
              to="/work"
              className="group grid grid-cols-12 items-baseline gap-4 py-7 border-b border-neutral-200"
              data-testid={`work-index-${w.client.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="col-span-2 md:col-span-1 text-xs font-medium tracking-[0.2em] text-neutral-400">0{i + 5}</span>
              <h3 className="col-span-10 md:col-span-5 font-display text-2xl md:text-3xl font-light tracking-tight text-black group-hover:text-brand-red transition-colors">{w.client}</h3>
              <p className="col-span-10 col-start-3 md:col-span-5 md:col-start-auto text-[11px] font-medium tracking-[0.15em] uppercase text-neutral-500">{w.services.join("  ·  ")}</p>
              <ArrowRight size={18} strokeWidth={1.25} className="hidden md:block col-span-1 justify-self-end text-neutral-300 group-hover:text-brand-red group-hover:translate-x-1 transition-all" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* 03 WHAT WE DO */
const OUTCOMES = [
  {
    title: "Attract",
    image: "/assets/exhibition-design.jpg",
    items: ["Branding", "Exhibitions", "3D Visualisation", "Product Communication"],
  },
  {
    title: "Engage",
    image: "/assets/kitchen-bath-industry-experience-ar1.jpg",
    items: ["Websites", "AR Experiences", "Digital Experiences", "Interactive Presentations"],
  },
  {
    title: "Convert",
    image: "/assets/vpv-dashboard-saa-vishnu.jpg",
    items: ["Virtual Plant Visit", "Sales Presentations", "Product Visualisation", "Digital Sales Tools"],
  },
];

const WhatWeDo = () => (
  <section className="border-b border-neutral-200" data-testid="what-we-do-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="03" title="What We Do" />
        <h2 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black" data-testid="what-we-do-heading">
          Attract. <span className="text-brand-red">Engage.</span> Convert.
        </h2>
        <p className="mt-5 text-lg font-light text-neutral-600 max-w-lg">
          Every capability we have exists to move a buyer through one of these three outcomes.
        </p>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8" data-testid="outcomes-grid">
        {OUTCOMES.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.08}>
            <div data-testid={`outcome-${o.title.toLowerCase()}`}>
              <div className="img-zoom aspect-[4/3] overflow-hidden">
                <img src={A(o.image)} alt={`${o.title} capability`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-light tracking-tight text-black border-t border-neutral-200 pt-5">
                <span className="text-brand-red">{o.title}</span>
              </h3>
              <ul className="mt-4 space-y-2.5">
                {o.items.map((it) => (
                  <li key={it} className="text-[15px] font-light text-neutral-600">{it}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* 04 VPV */
const VpvSection = () => (
  <section className="border-b border-neutral-200" data-testid="vpv-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-5">
        <Reveal>
          <SectionLabel num="04" title="Virtual Plant Visit" />
          <h2 className="mt-8 font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.1] text-black" data-testid="vpv-heading">
            Your buyer can&rsquo;t visit your factory. <span className="text-brand-red">Let your factory come to them.</span>
          </h2>
          <p className="mt-6 text-xl font-display font-light tracking-tight text-black">Real factories. Real confidence.</p>
          <p className="mt-5 text-lg font-light text-neutral-600 leading-relaxed">
            A buyer may like the product but still hesitate, because they have never seen the factory, the processes, the quality systems or the capability behind it. VPV lets them explore your plant remotely, in detail, before the first meeting.
          </p>
          <div className="mt-8">
            <ArrowCta to="/vpv" testid="vpv-explore-link">Explore VPV</ArrowCta>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.15}>
          <a
            href="https://kuula.co/share/5dBjd/collection/7ckpl?logo=-1&info=0&fs=1&vr=1&sd=1&autorotate=1.5&autop=10&thumbs=1"
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
            data-testid="vpv-section-image"
          >
            <div className="img-zoom overflow-hidden">
              <img src={A("/assets/vpv-dashboard-nandan.png")} alt="Nandan Virtual Plant Visit dashboard, click to explore the live tour" loading="lazy" decoding="async" className="w-full h-auto" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500 group-hover:text-brand-red transition-colors">
              Nandan · Virtual Plant Visit · Click to explore the live tour
            </p>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

/* 05 BRANDING */
const Branding = () => (
  <section className="border-b border-neutral-200" data-testid="branding-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="05" title="Branding" />
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <h2 className="lg:col-span-7 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black" data-testid="branding-heading">
            Look as good as<br />you <span className="text-brand-red">actually</span> are.
          </h2>
          <p className="lg:col-span-5 text-lg font-light text-neutral-600 leading-relaxed lg:pt-4 max-w-md">
            Established manufacturers often have excellent products, factories and capabilities. But the brand communication outside does not reflect the scale of the business. We close that gap.
          </p>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="branding-images">
        <Reveal>
          <Link to="/work" className="block group" data-testid="branding-image-0">
            <div className="img-zoom aspect-[3/4] overflow-hidden">
              <img src={A("/assets/invite-design.jpg")} alt="Invite and communication design" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500">Communication Design</p>
          </Link>
        </Reveal>
        <Reveal delay={0.08} className="sm:mt-14">
          <Link to="/work" className="block group" data-testid="branding-image-1">
            <div className="aspect-[3/4] bg-neutral-50 flex items-center justify-center p-10 overflow-hidden">
              <img src={A("/assets/business-card.jpg")} alt="Identity system design" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500">Identity Systems</p>
          </Link>
        </Reveal>
        <Reveal delay={0.16} className="sm:mt-28">
          <Link to="/work" className="block group" data-testid="branding-image-2">
            <div className="aspect-[3/4] bg-neutral-50 flex items-center justify-center p-10 overflow-hidden">
              <img src={A("/assets/linkedin-banner-design.jpg")} alt="Digital brand presence design" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500">Digital Presence</p>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

/* 06 3D VISUALISATION */
const ThreeD = () => (
  <section className="border-b border-neutral-200" data-testid="threed-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="06" title="3D Visualisation" />
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black" data-testid="threed-heading">
            When reality isn&rsquo;t enough,<br /><span className="text-brand-red">create it.</span>
          </h2>
          <p className="text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500 lg:text-right leading-loose">
            BMW India · NTPC · ABB<br />Airport Authority of India
          </p>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="threed-grid">
        <Reveal className="lg:col-span-7">
          <Link to="/work" className="block group" data-testid="threed-image-main">
            <div className="img-zoom aspect-[16/11] overflow-hidden">
              <img src={A("/assets/product-renders-bathtub-red-scaled.jpg")} alt="Photoreal 3D product visualisation" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </Link>
        </Reveal>
        <div className="lg:col-span-5 grid grid-rows-2 gap-6">
          <Reveal delay={0.08}>
            <Link to="/work" className="block group h-full" data-testid="threed-image-a">
              <div className="aspect-[16/9] lg:aspect-auto lg:h-full bg-neutral-50 flex items-center justify-center p-8 overflow-hidden">
                <img src={A("/assets/product-renders-faucet1-2-74-rgd-copy-scaled.webp")} alt="Product render, rose gold finish" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.16}>
            <Link to="/work" className="block group h-full" data-testid="threed-image-b">
              <div className="aspect-[16/9] lg:aspect-auto lg:h-full bg-neutral-50 flex items-center justify-center p-8 overflow-hidden">
                <img src={A("/assets/product-renders-hoist-12-high-res.jpg")} alt="Industrial product visualisation" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
              </div>
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* 07 AR EXPERIENCES */
const ArSection = ({ onLaunchAR }) => (
  <section className="border-b border-neutral-200" data-testid="ar-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <Reveal>
          <div className="grid grid-cols-2 gap-6">
            <div className="img-zoom aspect-[3/4] overflow-hidden" data-testid="ar-image-0">
              <img src={A("/assets/kitchen-bath-industry-experience-ar2.jpg")} alt="AR product experience in a real space" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="img-zoom aspect-[3/4] overflow-hidden mt-12" data-testid="ar-image-1">
              <img src={A("/assets/kitchen-bath-industry-experience-ar3.jpg")} alt="Interactive product demonstration" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6 order-1 lg:order-2">
        <Reveal>
          <SectionLabel num="07" title="AR Experiences" />
          <h2 className="mt-8 font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.1] text-black" data-testid="ar-heading">
            Don&rsquo;t just show the product. <span className="text-brand-red">Let people experience it.</span>
          </h2>
          <p className="mt-6 text-lg font-light text-neutral-600 leading-relaxed max-w-lg">
            AR places your product in the buyer&rsquo;s own space, on their own phone. No catalogue page can do that.
          </p>
          <div className="mt-10" data-testid="ar-live-buttons">
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500 mb-4">Try it live, best on your phone</p>
            <div className="flex flex-wrap gap-3">
              {AR_LINKS.slice(0, 3).map((a, i) => (
                <button
                  key={a.url}
                  type="button"
                  onClick={() => onLaunchAR(a)}
                  data-testid={`home-ar-link-${i}`}
                  className="border border-black text-black hover:bg-brand-red hover:border-brand-red hover:text-white px-6 py-3.5 text-[12px] font-medium uppercase tracking-[0.15em] transition-colors"
                >
                  {a.label}
                </button>
              ))}
            </div>
            <Link to="/work" className="mt-5 inline-block text-[12px] font-medium uppercase tracking-[0.15em] text-neutral-500 hover:text-brand-red transition-colors link-underline" data-testid="ar-more-link">
              11 live AR experiences on the Work page
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* 08 EXHIBITIONS + DIGITAL */
const ExhibitionsDigital = () => (
  <section className="border-b border-neutral-200" data-testid="exhibitions-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="08" title="Exhibitions + Digital" />
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <h2 className="lg:col-span-7 font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.1] text-black" data-testid="exhibitions-heading">
            The experience should continue <span className="text-brand-red">after the exhibition.</span>
          </h2>
          <p className="lg:col-span-5 text-lg font-light text-neutral-600 leading-relaxed lg:pt-3 max-w-md">
            We design the stall, the invites, the website and the digital follow-up as one system. The conversation that starts at your booth should not end there.
          </p>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start" data-testid="exhibitions-images">
        <Reveal className="lg:col-span-7">
          <Link to="/case-studies/param-dairy-branding" className="block group" data-testid="exhibitions-image-main">
            <div className="img-zoom aspect-[16/10] overflow-hidden">
              <img src={A("/assets/exhibition-design.jpg")} alt="Exhibition environment design" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500">Exhibition Design · Param Dairy</p>
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:mt-16">
          <Link to="/case-studies/param-dairy-branding" className="block group" data-testid="exhibitions-image-web">
            <div className="bg-neutral-50 flex items-center justify-center p-8 overflow-hidden">
              <img src={A("/assets/web.jpg")} alt="Website design for the same brand" loading="lazy" decoding="async" className="max-w-full h-auto object-contain" />
            </div>
            <p className="mt-4 text-[11px] font-medium tracking-[0.2em] uppercase text-neutral-500">Website · The same brand, online</p>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

/* 09 WHY THE NEXT DESIGN */
const Why = () => (
  <section className="border-b border-neutral-200" data-testid="why-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <SectionLabel num="09" title="Why The Next Design" />
        <h2 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black max-w-4xl" data-testid="why-heading">
          Design is not decoration.<br /><span className="text-brand-red">It should do something.</span>
        </h2>
      </Reveal>
      <div className="mt-16 border-t border-neutral-200" data-testid="why-rows">
        {[
          { t: "Attract.", d: "Get noticed." },
          { t: "Engage.", d: "Give people something worth exploring." },
          { t: "Convert.", d: "Help move the conversation forward." },
        ].map((r, i) => (
          <Reveal key={r.t} delay={i * 0.06}>
            <div className="grid grid-cols-12 items-baseline gap-4 py-8 border-b border-neutral-200" data-testid={`why-row-${i}`}>
              <span className="col-span-12 md:col-span-4 font-display text-3xl md:text-4xl font-light tracking-tight text-black">{r.t}</span>
              <span className="col-span-12 md:col-span-8 text-lg font-light text-neutral-600">{r.d}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* 10 NITIN GUPTA */
const Founder = () => (
  <section className="bg-neutral-950 text-white" data-testid="founder-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel num="10" title="Nitin Gupta" dark />
          <h2 className="mt-8 text-xs font-medium tracking-[0.3em] uppercase text-neutral-400" data-testid="founder-kicker">The person behind the work</h2>
          <p className="mt-6 font-display text-5xl sm:text-6xl font-light tracking-tight" data-testid="founder-name">Nitin Gupta</p>
          <p className="mt-2 text-[13px] font-medium tracking-[0.25em] uppercase text-brand-red">Founder, The Next Design</p>
          <p className="mt-8 text-xl font-light text-neutral-300 leading-relaxed max-w-xl">
            &ldquo;For more than 20 years, I have worked with manufacturers, brands and businesses to solve communication problems through design, technology and ideas.&rdquo;
          </p>
          <p className="mt-8 text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500" data-testid="founder-capabilities">
            Branding · 3D · AR · Exhibitions · Digital · VPV
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.15}>
          <img
            src={A("/assets/nitin-gupta-portrait.png")}
            alt="Nitin Gupta, Founder of The Next Design"
            loading="lazy"
            decoding="async"
            className="w-full h-auto max-w-md mx-auto"
            data-testid="founder-photo"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

/* 11 FINAL CTA */
const FinalCta = () => (
  <section className="bg-white" data-testid="final-cta-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black max-w-4xl" data-testid="final-cta-heading">
          You&rsquo;ve built the business.<br />Now let&rsquo;s build <span className="text-brand-red">the way it is seen.</span>
        </h2>
        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp-button"
            className="bg-brand-red hover:bg-black text-white px-8 py-4 text-[13px] font-medium uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-center gap-3"
          >
            <MessageCircle size={16} strokeWidth={1.75} /> WhatsApp Nitin <ArrowRight size={15} strokeWidth={1.5} />
          </a>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="final-cta-email-button"
            className="border border-black text-black hover:bg-black hover:text-white px-8 py-4 text-[13px] font-medium uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-center gap-3"
          >
            <Mail size={16} strokeWidth={1.75} /> Email Nitin <ArrowRight size={15} strokeWidth={1.5} />
          </a>
        </div>
        <p className="mt-6 text-[12px] font-light text-neutral-500 tracking-wide" data-testid="final-cta-note">
          WhatsApp for Indian manufacturers · Email for international companies · {SITE.email}
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const [arExp, setArExp] = useState(null);
  return (
    <div data-testid="home-page">
      <SEO
        title="The Next Design, Branding, 3D, AR, VPV & Exhibitions for Manufacturers"
        description="For manufacturers with multiple factories, products and export markets. The Next Design builds brands, experiences and digital tools that make big businesses look like one. Attract. Engage. Convert."
      />
      <Hero />
      <OurWork />
      <WhatWeDo />
      <VpvSection />
      <Branding />
      <ThreeD />
      <ArSection onLaunchAR={setArExp} />
      <ExhibitionsDigital />
      <Why />
      <Founder />
      <FinalCta />
      {arExp && <ARModal experience={arExp} onClose={() => setArExp(null)} />}
    </div>
  );
}
